import * as Sentry from '@sentry/react-router';
import { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import type { LinksFunction, MetaFunction } from 'react-router';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { RootErrorBoundary } from '~/components/errors';
import { LoadingLayout } from '~/components/layouts/loading-layout';
import { shouldIgnoreException } from '~/errors/ignored-sentry-messages';
import { useGridDensity, useGridFontSize } from '~/global-state/gridAppearance';
import { useAuthentication, useOrgChangeReload } from '~/hooks';
import { DEFAULT_LOCALE, useLocales } from '~/intl';
import { APP_NAME } from '~/utils/page-meta';
import type { Route } from './+types/root';
import { DialogViewport } from './components/dialogs/dialog-viewport';
import { ToastRenderer } from './contexts/toast';
import { useBackgroundQueries } from './hooks/useBackgroundQueries';
import { useGetUser } from './query-hooks';
import stylesheet from './styles.css?url';
import mapboxGeocodeStyleSheet from '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css?url';
import mapboxStylesheet from 'mapbox-gl/dist/mapbox-gl.css?url';
import skeletonStylesheet from 'react-loading-skeleton/dist/skeleton.css?url';
import dataGridStylesheet from '~/components/data-grid/styles.css?url';
import datePickerStylesheet from '~/components/date-pickers/base-date-picker/BaseDatePicker.css?url';
import dropdownStylesheet from '~/components/granular/dropdown/styles.css?url';
import geocoderStylesheet from '~/components/shared/geocoder/geocoder.css?url';

const googleFontsStylesheet =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Space+Grotesk:wght@500&display=swap';

const stylesheets = [
  stylesheet,
  datePickerStylesheet,
  mapboxStylesheet,
  mapboxGeocodeStyleSheet,
  geocoderStylesheet,
  dataGridStylesheet,
  dropdownStylesheet,
  skeletonStylesheet,
].map((href) => ({ href, rel: 'stylesheet' }));

export const links: LinksFunction = () => [
  { href: 'https://fonts.gstatic.com', rel: 'preconnect' },
  { href: googleFontsStylesheet, rel: 'stylesheet' },
  ...stylesheets,
];

export const meta: MetaFunction = () => [{ title: APP_NAME }];

export function Layout({ children }: { children: React.ReactNode }) {
  const { locale } = useLocales();
  const [gridDensity] = useGridDensity();
  const [gridFontSize] = useGridFontSize();

  // Applied imperatively rather than as JSX attributes: the SPA shell is prerendered with default
  // values, and React leaves mismatched <html> attributes untouched during hydration — so stored
  // preferences would only apply after the next re-render instead of on load.
  useEffect(() => {
    document.documentElement.dataset.gridDensity = gridDensity;
    document.documentElement.dataset.gridFontSize = gridFontSize;
    document.documentElement.dataset.locale = locale;
    document.documentElement.lang = locale;
  }, [gridDensity, gridFontSize, locale]);

  return (
    <html lang={DEFAULT_LOCALE}>
      <head>
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <Meta />
        <Links />
        {locale === 'ja' && (
          <link
            href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600&display=swap"
            rel="stylesheet"
          />
        )}
      </head>
      <body className="m-0 h-screen overflow-hidden">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  // Initialize authentication and user data
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthentication();
  const { isLoading: isUserLoading } = useGetUser();
  useOrgChangeReload();

  // Prefetch filter values and poll job operations in the background
  useBackgroundQueries(!isAuthLoading && !isUserLoading);

  if (isAuthLoading || isUserLoading || !isAuthenticated) {
    return null;
  }

  return (
    <ToastRenderer>
      <Outlet />
      <DialogViewport />
    </ToastRenderer>
  );
}

export const HydrateFallback = LoadingLayout;

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const { locale, messages } = useLocales();

  if (!isRouteErrorResponse(error) && !shouldIgnoreException(error as Error)) {
    Sentry.captureException(error);
  }

  return (
    <IntlProvider defaultLocale={DEFAULT_LOCALE} locale={locale} messages={messages}>
      <RootErrorBoundary />
    </IntlProvider>
  );
}
